'use client';

import { useBreakpoint } from 'hooks/useBreakpoints';
import { useLeadGenerationInteraction } from './useLeadGenerationInteraction';

import { LEAD_GENERATION_INTERACTION_TYPES } from '../types';

type ActionDetailValues =
  | 'Navigation Viewed'
  | 'Sidebar Opened'
  | 'Sidebar Closed'
  | 'Sidebar Submenu Opened'
  | 'Sidebar Submenu Closed'
  | 'Horizontal Submenu Closed'
  | 'Horizontal Submenu Opened';
interface NavigationTrackProps {
  location?: string;
  action_detail?: string;
  level?: number;
}

interface NavigationMenuTracking {
  track: (updatedProps: NavigationTrackProps) => void;
}

const useNavigationMenuTracking = (
  initialProps?: NavigationTrackProps,
  autoTrack: boolean = false,
): NavigationMenuTracking => {
  const { isAboveMd } = useBreakpoint('md');

  const getAdditionalData = (updatedLevel: number = 0) => [
    {
      key: 'action',
      value: 'click',
    },
    {
      key: 'screen',
      value: isAboveMd ? 'desktop' : 'mobile',
    },
    {
      key: 'level',
      value: updatedLevel,
    },
  ];

  // creates a skeleton to reduce duplication of existing parts
  const trackingProps = {
    interaction_type: LEAD_GENERATION_INTERACTION_TYPES.NAVIGATION_BAR_INTERACTION,
    interaction_details: {
      location: initialProps?.location ?? 'Navigation Sidebar',
      action_detail: initialProps?.action_detail ?? 'Navigation Viewed',
    },
    additional_data: getAdditionalData(initialProps?.level ?? 0),
  };

  // use the base hook for this, as this is the practical event type
  const trackNavigationInteraction = useLeadGenerationInteraction(trackingProps, autoTrack);
  const isActionDetailValid = (actionDetail: string): actionDetail is ActionDetailValues => {
    return [
      'Navigation Viewed',
      'Sidebar Opened',
      'Sidebar Closed',
      'Sidebar Submenu Opened',
      'Sidebar Submenu Closed',
      'Horizontal Submenu Closed',
      'Horizontal Submenu Opened',
    ].includes(actionDetail);
  };

  return {
    track: (updatedProps: NavigationTrackProps): void => {
      if (!isActionDetailValid(updatedProps?.action_detail ?? '')) {
        return;
      }

      trackNavigationInteraction({
        ...trackingProps,
        interaction_details: {
          location: updatedProps.location ?? 'Navigation Sidebar',
          action_detail: updatedProps.action_detail ?? 'click',
        },
        additional_data: getAdditionalData(updatedProps.level),
      });
    },
  };
};

export default useNavigationMenuTracking;
